import React, { useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Heading from 'components/Luxkit/Typography/Heading'
import FlashCancellationPolicies from 'components/Common/CancellationPolicies/Flash/FlashCancellationPolicies'
import useToggle from 'hooks/useToggle'
import { take } from 'lib/array/arrayUtils'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import MultipleRoomsBookingModalPriceDetails from './MultipleRoomsBookingModalPriceDetails'
import useIsAvailableForPurchase from '../hooks/useIsAvailableForPurchase'
import LuxuryPlusInclusions from 'loyalty/components/LuxuryPlusInclusions'
import Group from 'components/utils/Group'
import TextLink from 'components/Luxkit/TextLink'
import InclusionsV2Section from '../FlashHotel/InclusionsV2Section'
import InclusionsSection from '../FlashHotel/InclusionsSection'
import { useOfferInclusions } from 'hooks/useOfferInclusions'

const PackageOption = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${rem(16)};
  border-top: 1px solid ${props => props.theme.palette.neutral.default.five};

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 0.6fr;
    grid-gap: ${rem(24)};
  }
`

const DetailsSection = styled.div`
  display: flex;
  flex-direction: column;

  grid-column: 1;
  grid-row: 1;
  padding: ${rem(16)} ${rem(24)};

  > * + * {
    margin-top: ${rem(8)};
  }

  ${mediaQueryUp.desktop} {
    grid-column: 1;
    grid-row: 1;
    padding: ${rem(16)};
  }
`

const PriceSection = styled.div`
  display: flex;
  flex-direction: column;

  grid-column: 1;
  grid-row: 2;
  padding: 0 ${rem(24)} ${rem(16)};

  ${mediaQueryUp.tablet} {
    align-items: flex-start;
    grid-column: 1 / span 2;
    padding: ${rem(16)} ${rem(24)};
  }

  ${mediaQueryUp.desktop} {
    grid-column: 2;
    grid-row: 1;
    padding: ${rem(16)};
  }
`

interface Props {
  offer: App.Offer;
  packageView: OfferPackageOptionView;
  checkIn: moment.Moment;
  duration: number;
  selectedPackageUniqueKey: string;
  room: App.RoomOccupants;
  onSelect: (packageOption: App.Package) => void;
}

const NUM_INCLUSIONS_TO_SHOW = 3

function MultipleRoomsBookingModalOption(props: Props) {
  const {
    offer,
    packageView,
    checkIn,
    duration,
    selectedPackageUniqueKey,
    room,
    onSelect,
  } = props

  const pkg = packageView.package
  const [showMoreInclusion, onToggleInclusions] = useToggle()
  const checkOut = checkIn?.clone().add(duration, 'days')

  const { inclusions, bonusInclusions, loyaltyInclusions } = useOfferInclusions(pkg, { checkIn, checkOut })
  const hasLoyaltyInclusions = loyaltyInclusions.length > 0
  const inclusionsV1ToShow = useMemo(() => showMoreInclusion ? inclusions : take(inclusions, NUM_INCLUSIONS_TO_SHOW), [inclusions, showMoreInclusion])
  const hasInclusionsV1 = pkg.inclusions && pkg.inclusions.length > 0

  const totalInclusionsCount = inclusions.length + bonusInclusions.length

  const availableForPurchase = useIsAvailableForPurchase()
  return (
    <PackageOption data-testid="package-option">
      <DetailsSection>
        <div>
          <Heading variant="heading6">{pkg.name}</Heading>
          {availableForPurchase && <FlashCancellationPolicies
            checkInDate={checkIn}
            propertyTimezoneOffset={offer.property?.timezoneOffset}
            cancellationPolicy={pkg.roomRate?.cancellationPolicy}
          />}
        </div>
        <Group direction="vertical" gap={8}>
          {hasLoyaltyInclusions && <LuxuryPlusInclusions loyaltyInclusions={loyaltyInclusions} accommodationOffer={offer} />}
          {hasInclusionsV1 && <InclusionsSection
            inclusions={inclusionsV1ToShow}
            bonusInclusions={showMoreInclusion ? bonusInclusions : undefined}
          />}
          {!hasInclusionsV1 && pkg.inclusionsV2 && <InclusionsV2Section inclusions={take(pkg.inclusionsV2, showMoreInclusion ? pkg.inclusionsV2.length : NUM_INCLUSIONS_TO_SHOW)} />}
          {totalInclusionsCount > NUM_INCLUSIONS_TO_SHOW &&
            <TextLink size="medium" weight="bold" onClick={onToggleInclusions}>
              {showMoreInclusion ? 'View less inclusions' : `+ ${totalInclusionsCount - NUM_INCLUSIONS_TO_SHOW} more inclusions`}
            </TextLink>
          }
        </Group>
      </DetailsSection>
      <PriceSection>
        <MultipleRoomsBookingModalPriceDetails
          room={room}
          packageView={packageView}
          isSelected={selectedPackageUniqueKey === pkg.uniqueKey}
          onSelect={onSelect}
        />
      </PriceSection>
    </PackageOption>
  )
}

export default withOfferPageAndReduxState((state) => ({
  checkIn: state.checkInDate,
  duration: state.duration,
}))(MultipleRoomsBookingModalOption)
